import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { PhoneInput } from '@sage/ui-components';
import { createFileRoute, useNavigate, useParams, useRouter } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import { useModalManager } from 'src/components/modals/ModalManager';
import RevokeAccessModal from 'src/components/modals/RevokeAccessModal';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useUpdatePersonDetailsMutation } from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/care/team/member/edit/$teamMemberRid')({
    component: EditAccountComponent,
});

const PageName = 'Edit Team Member Page';

function EditAccountComponent() {
    const navigate = useNavigate();
    const { teamMemberRid } = useParams({ from: '/(app)/care/team/member/edit/$teamMemberRid' });
    const { history } = useRouter();

    const { trackPageView } = useAnalytics();
    const { showSuccess, showError } = useSnackbar();
    const { openModal } = useModalManager();

    const { getTeamMemberDetails, currentUser } = useTeamMemberData();
    const teamMember = getTeamMemberDetails(teamMemberRid);

    const { handleSubmit, control, formState, setValue } = useForm<{
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
        role: string;
        isTeamAdmin: boolean;
    }>({
        mode: 'onChange',
        defaultValues: {
            firstName: teamMember?.firstName,
            lastName: teamMember?.lastName,
            phoneNumber: teamMember?.phoneNumber,
            email: teamMember?.email,
            role: teamMember?.role || '',
            isTeamAdmin: teamMember?.isTeamAdmin || false,
        },
    });

    const { isDirty, isValid } = formState;

    const phoneNumber = useWatch({ control, name: 'phoneNumber' });
    const [updatePersonDetails, { isLoading: isUpdatingPerson }] = useUpdatePersonDetailsMutation();

    const isUpdating = isUpdatingPerson;

    // Update form values when teamMember data loads
    useEffect(() => {
        if (teamMember) {
            setValue('firstName', teamMember.firstName, { shouldDirty: false });
            setValue('lastName', teamMember.lastName, { shouldDirty: false });
            setValue('phoneNumber', teamMember.phoneNumber, { shouldDirty: false });
            setValue('email', teamMember.email, { shouldDirty: false });
            setValue('role', teamMember.role || '', { shouldDirty: false });
            setValue('isTeamAdmin', teamMember.isTeamAdmin || false, { shouldDirty: false });
        }
    }, [teamMember, setValue]);

    const onSubmit = async (data: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        role: string;
        isTeamAdmin: boolean;
    }) => {
        if (!teamMember) return;
        if (!teamMember.teamMemberRid) {
            showError('Team member information is missing.');
            return;
        }

        try {
            // Update team member details
            await updatePersonDetails({
                personRid: teamMember.personRid,
                updatePersonRequest: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phoneNumber: data.phoneNumber,
                    email: data.email,
                },
            }).unwrap();

            // TODO(@sage): Add role update when supported by API
            // await updateTeamMember({
            //     teamMemberRid: teamMember.teamMemberRid,
            //     updateTeamMemberRequest: {
            //         isAdmin: data.isTeamAdmin,
            //     },
            // }).unwrap();

            showSuccess('Team member updated');
            history.back();
        } catch (error) {
            console.error('Failed to update team member details:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleOpenRevokeAccessModal = () => {
        openModal({
            content: RevokeAccessModal,
            props: {
                teamMember,
                onSuccess: () => {
                    navigate({ to: '/care/team', replace: true });
                },
            },
        });
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle="Edit Account" />
            {teamMember && (
                <Stack sx={{ gap: 4 }}>
                    <Stack sx={{ gap: 2, alignItems: 'center' }}>
                        <TeamMemberAvatar teamMember={teamMember} size="large" />
                        <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="h2" component="h2" sx={{ fontWeight: 600 }}>
                                {teamMember.firstName} {teamMember.lastName}
                            </Typography>
                            {teamMember?.isTeamAdmin && (
                                <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                                    <FontAwesomeIcon icon={faLock} size="sm" />
                                    <Typography variant="body2">Team Admin</Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>

                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                            <TextField
                                inputRef={ref}
                                fullWidth
                                label="First Name"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                        rules={{ required: 'First name is required' }}
                    />

                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                            <TextField
                                inputRef={ref}
                                fullWidth
                                label="Last Name"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                        rules={{ required: 'Last name is required' }}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                            <TextField
                                inputRef={ref}
                                fullWidth
                                label="Email"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                        rules={{
                            required: 'Email is required',
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                            },
                        }}
                    />

                    <PhoneInput
                        value={phoneNumber}
                        onChange={(value) => setValue('phoneNumber', value)}
                        error={!!formState.errors.phoneNumber}
                        helperText={formState.errors.phoneNumber?.message}
                    />

                    {currentUser?.isTeamAdmin &&
                        teamMember.teamMemberRid !== currentUser.teamMemberRid && (
                            <Button
                                variant="contained"
                                fullWidth
                                color="error"
                                onClick={handleOpenRevokeAccessModal}
                            >
                                Revoke Access
                            </Button>
                        )}
                </Stack>
            )}
            <PageFooter>
                <Stack>
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={handleSubmit(onSubmit)}
                        disabled={!isDirty || !isValid}
                        loading={isUpdating}
                    >
                        Save Updates
                    </Button>
                </Stack>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
